import { HeadingPriority } from "atoms/constants/heading-priority";
import { Icons } from "atoms/constants/icons";
import { ParagraphSizes } from "atoms/constants/paragraph-sizes";
import Trans from "atoms/i18n/trans";
import Icon from "atoms/icons/icon";
import ProgressSlots from "atoms/progress/progress-slots";
import Heading from "atoms/typography/heading";
import Paragraph from "atoms/typography/paragraph";
import AlertLevels from "constants/alert-levels";
import MemoryRecord from "models/view-models/memories/memory-record";
import AlertBanner from "molecules/alerts/alert-banner";
import { ModalTypes } from "molecules/constants/modal-types";
import BaseModalFooter from "molecules/modals/footers/base-modal-footer";
import BaseModalHeader from "molecules/modals/headers/base-modal-header";
import Modal from "molecules/modals/modal";
import ChatMemoryItem from "organisms/modals/chat-memories-modal/chat-memory-item";
import React, { useEffect } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import { MAX_MEMORIES_PER_USER } from "utilities/hooks/domain/chat/use-memories";
import useBreakpoint from "utilities/hooks/use-breakpoint";
import { t } from "utilities/localization-utils";
import { ToastManager } from "utilities/toast/toast-manager";
import MaxMemoriesAlert from "./max-memories-alert";

interface ChatMemoriesModalProps {
    memories?: MemoryRecord[];
    refetchMemories: () => void;
    isOpen: boolean;
    onClose: () => void;
}

const CSS_CLASS_NAME = "c-chat-memories-modal";

const ChatMemoriesModal = ({
    isOpen,
    onClose,
    refetchMemories,
    memories = [],
}: ChatMemoriesModalProps) => {
    const isDesktop = useBreakpoint(Breakpoints.Tablet);
    const handleDeleteError = () => {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("deleting"),
                resource: t("memory"),
            })
        );
    };

    useEffect(() => {
        if (isOpen) {
            refetchMemories();
        }
    }, [isOpen, refetchMemories]);
    return (
        <Modal
            cssClassName={CSS_CLASS_NAME}
            closeDialog={onClose}
            isVisible={isOpen}
            label={t("memories")}
            type={isDesktop ? ModalTypes.Base : ModalTypes.Bottom}>
            <BaseModalHeader
                closeDialog={onClose}
                heading={t("chat-memories-modal--header")}
            />
            <section className={`${CSS_CLASS_NAME}__body c-modal__body`}>
                <Paragraph
                    cssClassName={`${CSS_CLASS_NAME}__subtitle`}
                    size={ParagraphSizes.Small}>
                    {t("chat-memories-modal--subtitle")}
                </Paragraph>
                <div className={`${CSS_CLASS_NAME}__memories-used`}>
                    <Paragraph
                        cssClassName={`${CSS_CLASS_NAME}__memories-used-message`}>
                        <Icon
                            type={Icons.Sparkle}
                            cssClassName={`${CSS_CLASS_NAME}__memories-used-message-icon`}
                        />
                        {t("count-of-total-memories-used", {
                            count: memories.length,
                            total: MAX_MEMORIES_PER_USER,
                        })}
                    </Paragraph>
                    <ProgressSlots
                        totalSlots={MAX_MEMORIES_PER_USER}
                        filledSlots={memories.length}
                    />
                </div>

                {memories.length >= MAX_MEMORIES_PER_USER - 1 && (
                    <MaxMemoriesAlert
                        maxMemories={MAX_MEMORIES_PER_USER}
                        memoryCount={memories.length}
                    />
                )}

                {CollectionUtils.hasValues(memories) ? (
                    <ul className={`${CSS_CLASS_NAME}__memories-list`}>
                        {memories.map((memory) => (
                            <ChatMemoryItem
                                key={memory.id}
                                memory={memory}
                                onSuccess={refetchMemories}
                                onError={handleDeleteError}
                            />
                        ))}
                    </ul>
                ) : (
                    <section
                        className={`${CSS_CLASS_NAME}__alert-banner-section`}>
                        <AlertBanner alertLevel={AlertLevels.Info} hideIcon>
                            <Heading
                                cssClassName={`${CSS_CLASS_NAME}__alert-banner-heading`}
                                priority={HeadingPriority.Three}>
                                <Icon type={Icons.Brain} />
                                What are memories?
                            </Heading>
                            <Paragraph
                                cssClassName={`${CSS_CLASS_NAME}__alert-banner-paragraph`}
                                size={ParagraphSizes.Small}>
                                {t("chat-memories-modal--about-memories")}
                            </Paragraph>
                            <Paragraph
                                cssClassName={`${CSS_CLASS_NAME}__alert-banner-paragraph`}
                                size={ParagraphSizes.Small}>
                                <Trans key={"chat-memories-modal--try-it-out"}>
                                    <strong>Want to give it a try?</strong> In
                                    the chat, tell CASI about yourself and how
                                    you like to work. For example,{" "}
                                    <em>
                                        "always use the 2023 edition of NFPA 70"
                                    </em>{" "}
                                    or{" "}
                                    <em>
                                        "remember that my jurisdiction is
                                        Boston, MA"
                                    </em>
                                    .
                                </Trans>
                            </Paragraph>
                        </AlertBanner>
                    </section>
                )}
            </section>
            <BaseModalFooter
                primaryButtonProps={{ onClick: onClose }}
                primaryText={t("done")}
                closeDialog={onClose}
            />
        </Modal>
    );
};

export default ChatMemoriesModal;
